/**
 * ManualDealDialog — create a manual deal draft with optional "Improve with AI"
 * on each empty text field.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Plus, Sparkles, Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { useAuth } from '../../context/AuthContext';

const DEAL_TYPES = [
  { value: 'footfall_filler', label: 'Footfall Filler' },
  { value: 'basket_builder', label: 'Basket Builder' },
  { value: 'community_event', label: 'Community Event' },
  { value: 'local_group', label: 'Local Group' },
  { value: 'waste_rescue', label: 'Waste Rescue' },
  { value: 'repeat_visit', label: 'Repeat Visit' },
  { value: 'weather_triggered', label: 'Weather Triggered' },
  { value: 'seasonal_promotion', label: 'Seasonal' },
];

export default function ManualDealDialog({ open, onOpenChange, locationId, onCreated, initial }) {
  const { api } = useAuth();
  const [form, setForm] = useState({
    name: '',
    deal_type: 'footfall_filler',
    goal: '',
    target_customer: '',
    start_date: '',
    end_date: '',
    normal_price: '',
    deal_price: '',
    discount_value: '',
    pos_tracking_name: '',
    customer_copy: '',
    staff_instruction: '',
    ...(initial || {}),
  });
  const [busy, setBusy] = useState(false);
  const [improvingField, setImprovingField] = useState(null);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const reset = () => setForm({
    name: '', deal_type: 'footfall_filler', goal: '', target_customer: '',
    start_date: '', end_date: '', normal_price: '', deal_price: '', discount_value: '',
    pos_tracking_name: '', customer_copy: '', staff_instruction: '',
  });

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        location_id: locationId,
        normal_price: form.normal_price === '' ? null : Number(form.normal_price),
        deal_price: form.deal_price === '' ? null : Number(form.deal_price),
      };
      await api.post('/deals', payload);
      toast.success('Deal created');
      reset();
      onCreated?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create deal');
    } finally { setBusy(false); }
  };

  const improve = async (field) => {
    // Improve-with-AI only works on already-saved deals. For manual dialog
    // (unsaved), we create-then-improve as one flow.
    setImprovingField(field);
    try {
      // Save minimal first
      const payload = { ...form, location_id: locationId };
      const created = await api.post('/deals', payload);
      const res = await api.post(`/deals/${created.data.id}/improve-with-ai`, { field });
      setField(field, res.data.value || '');
      toast.success('AI suggestion applied — draft saved');
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI could not improve this field');
    } finally { setImprovingField(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="manual-deal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5" /> New Manual Deal
          </DialogTitle>
          <DialogDescription>
            Draft a deal by hand. Use the AI helpers to fill weak fields.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="m-name">Deal name *</Label>
            <Input id="m-name" value={form.name} onChange={(e) => setField('name', e.target.value)} data-testid="manual-name" />
          </div>
          <div>
            <Label>Deal type</Label>
            <Select value={form.deal_type} onValueChange={(v) => setField('deal_type', v)}>
              <SelectTrigger data-testid="manual-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DEAL_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="m-goal">Goal</Label>
            <Textarea id="m-goal" rows={2} value={form.goal}
              onChange={(e) => setField('goal', e.target.value)}
              data-testid="manual-goal" />
          </div>
          <div>
            <Label htmlFor="m-customer">Target customer</Label>
            <Input id="m-customer" value={form.target_customer}
              onChange={(e) => setField('target_customer', e.target.value)}
              data-testid="manual-customer" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="m-start">Start date</Label>
              <Input id="m-start" type="date" value={form.start_date}
                onChange={(e) => setField('start_date', e.target.value)} />
            </div>
            <div>
              <Label htmlFor="m-end">End date</Label>
              <Input id="m-end" type="date" value={form.end_date}
                onChange={(e) => setField('end_date', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="m-normal">Normal price (£)</Label>
              <Input id="m-normal" type="number" step="0.01" value={form.normal_price}
                onChange={(e) => setField('normal_price', e.target.value)} />
            </div>
            <div>
              <Label htmlFor="m-deal">Deal price (£)</Label>
              <Input id="m-deal" type="number" step="0.01" value={form.deal_price}
                onChange={(e) => setField('deal_price', e.target.value)} />
            </div>
            <div>
              <Label htmlFor="m-disc">Discount label</Label>
              <Input id="m-disc" placeholder="e.g. Save £1.50"
                value={form.discount_value}
                onChange={(e) => setField('discount_value', e.target.value)} />
            </div>
          </div>
          <div>
            <Label htmlFor="m-track">POS tracking name / coupon</Label>
            <Input id="m-track" value={form.pos_tracking_name}
              onChange={(e) => setField('pos_tracking_name', e.target.value)}
              placeholder="Unique SKU or coupon code (required for measurement)"
              data-testid="manual-tracking" />
          </div>
          <div>
            <Label htmlFor="m-copy" className="flex items-center justify-between">
              <span>Customer copy</span>
              <Button type="button" size="sm" variant="ghost" onClick={() => improve('customer_copy')} disabled={!!improvingField}>
                {improvingField === 'customer_copy' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                <span className="ml-1 text-xs">Improve with AI</span>
              </Button>
            </Label>
            <Textarea id="m-copy" rows={2} value={form.customer_copy}
              onChange={(e) => setField('customer_copy', e.target.value)} />
          </div>
          <div>
            <Label htmlFor="m-staff" className="flex items-center justify-between">
              <span>Staff instruction</span>
              <Button type="button" size="sm" variant="ghost" onClick={() => improve('staff_instruction')} disabled={!!improvingField}>
                {improvingField === 'staff_instruction' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                <span className="ml-1 text-xs">Improve with AI</span>
              </Button>
            </Label>
            <Textarea id="m-staff" rows={2} value={form.staff_instruction}
              onChange={(e) => setField('staff_instruction', e.target.value)} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="manual-save-btn">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            Save Draft
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
